import React from 'react';
import {
  AgnesAutocomplete,
  AgnesBadge,
  AgnesButton,
  AgnesDataTable,
  type AgnesDataTableColumn,
  AgnesIconButton,
  AgnesLabel,
  AgnesModal,
  AgnesPageHeader,
  AgnesPopover,
  AgnesPopoverContent,
  AgnesPopoverTrigger,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesTabs,
  AgnesTabsContent,
  AgnesTabsList,
  AgnesTabsTrigger,
  AgnesTooltip,
} from '@agnes/ui';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import {
  ChevronDown,
  ChevronUp,
  Download,
  Eye,
  Filter,
  Info,
  ShieldAlert,
  Trash2,
  X,
} from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import { CardTitle } from '@/components/ui/SectionCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { SearchInput } from '@/components/SearchInput';
import {
  type AgentOption,
  type CategoriaOption,
  useAgentesNocivosStore,
} from '@/stores/useAgentesNocivosStore';
import { useEmpregadoresStore } from '@/stores/useEmpregadoresStore';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { ALIQUOT_LABEL, type RetirementAliquot, situationBadge } from '@/lib/agentesNocivos';
import { countFiltros, labelFiltro } from '@/lib/filtros';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

const SELECT_TRIGGER =
  '[&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

type Any = any;

// mapeia label -> {year,aliquot} para o filtro "Contribuição / Alíquota" (ignora 'nothing')
const ALIQUOT_TIERS: RetirementAliquot[] = [
  { year: '25', aliquot: '6' },
  { year: '20', aliquot: '9' },
  { year: '15', aliquot: '12' },
];
const aliquotKey = (t: RetirementAliquot) => `${t.year}y-${t.aliquot}%`;
const ALIQUOT_TIER_OPTIONS = ALIQUOT_TIERS.map((t) => ({
  value: aliquotKey(t),
  label: ALIQUOT_LABEL[aliquotKey(t)] ?? aliquotKey(t),
}));

const SIM_NAO_OPTIONS = [
  { value: 'sim', label: 'Sim' },
  { value: 'nao', label: 'Não' },
];

// mapeia a cor semântica do situationBadge (legado) -> variant do AgnesBadge
const SITUATION_VARIANT: Record<string, Any> = {
  green: 'success',
  red: 'error',
  yellow: 'warning',
  white: 'secondary',
  brown: 'secondary',
  grey: 'secondary',
};

function SituacaoBadge({ situation }: { situation?: string }) {
  const meta = situationBadge(situation);
  return (
    <AgnesBadge variant={SITUATION_VARIANT[meta.color] ?? 'secondary'}>{meta.label}</AgnesBadge>
  );
}

function SelectField({
  placeholder,
  value,
  onValueChange,
  options,
  required,
}: {
  placeholder: string;
  value: string;
  onValueChange: (v: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
}) {
  return (
    <div>
      <AgnesLabel required={required}>{placeholder}</AgnesLabel>
      <AgnesSelect value={value} onValueChange={onValueChange}>
        <AgnesSelectTrigger className={SELECT_TRIGGER}>
          <AgnesSelectValue placeholder={`Selecione ${placeholder.toLowerCase()}`} />
        </AgnesSelectTrigger>
        <AgnesSelectContent>
          {options.map((o) => (
            <AgnesSelectItem key={o.value} value={o.value}>
              {o.label}
            </AgnesSelectItem>
          ))}
        </AgnesSelectContent>
      </AgnesSelect>
    </div>
  );
}

// popover do Filtro geral (Competência* / Empresa / Categoria)
function FiltroGeral() {
  const {
    competency,
    cnpjs,
    categories,
    competencias,
    categoriesList,
    setCompetency,
    setCnpjs,
    setCategories,
    applyFilter,
  } = useAgentesNocivosStore();
  const empregadores = useEmpregadoresStore((s) => s.lista);
  // competency nasce setada (competência mais recente no init), então não conta como filtro
  // aplicado pelo usuário — só empresa/categoria entram na contagem.
  const nFiltros = countFiltros({ competency, cnpjs, categories }, { ignore: ['competency'] });
  const [open, setOpen] = React.useState(false);
  const [comp, setComp] = React.useState(competency ?? '');
  const [emps, setEmps] = React.useState<Any[]>(
    empregadores.filter((e) => cnpjs.includes(e.cnpjFilial)),
  );
  const [cats, setCats] = React.useState<CategoriaOption[]>(
    categoriesList.filter((c) => categories.includes(Number(c.codigo))),
  );

  // a competência default chega de forma assíncrona (init carrega depois do mount),
  // então ressincroniza ao abrir — mesmo motivo do TrabalhadoresFilter
  React.useEffect(() => {
    if (!open) return;
    setComp(competency ?? '');
    setEmps(empregadores.filter((e) => cnpjs.includes(e.cnpjFilial)));
    setCats(categoriesList.filter((c) => categories.includes(Number(c.codigo))));
  }, [open, competency, cnpjs, categories, empregadores, categoriesList]);

  const competenciaOpts = competencias.map((c) => ({ value: c, label: c }));

  const toggleEmp = (e: Any) => {
    setEmps((prev) =>
      prev.some((p) => p.cnpjFilial === e.cnpjFilial)
        ? prev.filter((p) => p.cnpjFilial !== e.cnpjFilial)
        : [...prev, e],
    );
  };
  const toggleCat = (c: CategoriaOption) => {
    setCats((prev) =>
      prev.some((p) => p.codigo === c.codigo)
        ? prev.filter((p) => p.codigo !== c.codigo)
        : [...prev, c],
    );
  };

  const aplicar = async () => {
    setCompetency(comp || null);
    setCnpjs(emps.map((e) => e.cnpjFilial));
    setCategories(cats.map((c) => Number(c.codigo)));
    await applyFilter();
    setOpen(false);
  };
  const limpar = () => {
    setComp('');
    setEmps([]);
    setCats([]);
  };

  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          startIcon={<Filter className="h-4 w-4" />}
          aria-label={nFiltros > 0 ? `Filtro, ${nFiltros} ativos` : 'Filtro'}
        >
          {labelFiltro('Filtro', nFiltros)}
        </AgnesButton>
      </AgnesPopoverTrigger>
      <AgnesPopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-agnes-border px-4 py-3">
          <span className="text-base font-semibold text-agnes-text">Filtro</span>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-3 px-4 py-4">
          <SelectField
            placeholder="Competência"
            required
            value={comp}
            onValueChange={setComp}
            options={competenciaOpts}
          />
          <div>
            <AgnesLabel>Empresa</AgnesLabel>
            <AgnesAutocomplete
              options={empregadores}
              value={emps}
              onOptionToggle={toggleEmp}
              getOptionLabel={(e: Any) => e.socialName || e.company || e.cnpjFilial}
              isOptionEqualToValue={(a: Any, b: Any) => a.cnpjFilial === b.cnpjFilial}
              placeholder="Selecione a empresa"
              noOptionsText="Nenhuma empresa"
            />
          </div>
          <div>
            <AgnesLabel>Categoria</AgnesLabel>
            <AgnesAutocomplete
              options={categoriesList}
              value={cats}
              onOptionToggle={toggleCat}
              getOptionLabel={(c: CategoriaOption) => `${c.codigo} - ${c.descricao}`}
              isOptionEqualToValue={(a: CategoriaOption, b: CategoriaOption) =>
                a.codigo === b.codigo
              }
              placeholder="Selecione a categoria"
              noOptionsText="Nenhuma categoria"
            />
          </div>
        </div>
        <div className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3">
          <AgnesButton variant="primary" disabled={!comp} onClick={aplicar} className="w-full">
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            variant="outlined"
            onClick={limpar}
            startIcon={<Trash2 className="h-4 w-4" />}
            className="w-full"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}

// card de estatística com donut (conic-gradient, padrão usado nas demais telas do produto)
function StatDonut({
  title,
  value,
  pctLabel,
  onClick,
  disabled,
}: {
  title: string;
  value: number;
  pctLabel: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const clickable = Boolean(onClick) && !disabled;
  return (
    <button
      type="button"
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className={`flex items-center justify-between rounded-xl bg-agnes-primary/5 p-6 text-left ${clickable ? 'cursor-pointer transition hover:bg-agnes-primary/10' : 'cursor-default opacity-60'}`}
    >
      <div>
        <p className="mb-3 text-lg font-medium text-agnes-primary">{title}</p>
        <p className="text-4xl font-bold text-agnes-text">{value}</p>
        <p className="text-sm text-agnes-text-muted">{pctLabel}</p>
      </div>
      <div className="h-24 w-24 rounded-full border-[12px] border-white" />
    </button>
  );
}

const ENSEJADOR_APOSENTADORIA_INFO = (
  <div>
    <p className="font-semibold">
      Financiamento da Aposentadoria Especial (FAE) e Redução do Tempo de Contribuição.
    </p>
    <p className="mt-1">
      A aposentadoria especial é um benefício de caráter continuado, devido ao segurado da
      Previdência Social que tenha trabalhado durante 15, 20 ou 25 anos, conforme cada caso, sujeito
      a condições especiais que prejudiquem a sua saúde ou a integridade física.
    </p>
    <ul className="mt-1 list-disc space-y-0.5 pl-4">
      <li>FAE15_12% (15 anos de contribuição e alíquota de 12%)</li>
      <li>FAE20_09% (20 anos de contribuição e alíquota de 9%)</li>
      <li>FAE25_06% (25 anos de contribuição e alíquota de 6%)</li>
    </ul>
  </div>
);

// donut genérico via conic-gradient (mesmo padrão de PageTrabalhadorAfastamento)
function Donut({
  title,
  series,
  labels,
  colors,
  info,
}: {
  title: string;
  series: number[];
  labels: string[];
  colors: string[];
  info?: React.ReactNode;
}) {
  const total = series.reduce((a, b) => a + b, 0);
  const conic = (() => {
    if (!total) return `conic-gradient(${colors[0] ?? '#E7E7FA'} 0% 100%)`;
    let acc = 0;
    const stops = series
      .map((v, i) => ({ v, c: colors[i] }))
      .filter((s) => s.v > 0)
      .map((s) => {
        const from = (acc / total) * 100;
        acc += s.v;
        const to = (acc / total) * 100;
        return `${s.c} ${from}% ${to}%`;
      });
    return `conic-gradient(${stops.join(', ')})`;
  })();

  return (
    <div className="rounded-xl border border-agnes-border bg-agnes-bg-default p-4 shadow-agnes-card">
      <div className="mb-4 flex items-center justify-between">
        <CardTitle>{title}</CardTitle>
        {info && (
          <AgnesTooltip className="max-w-sm whitespace-normal" content={info}>
            <Info
              className="h-4 w-4 text-agnes-primary"
              aria-label={`Ajuda ${title}`}
              data-qa="agentes-nocivos-ensejador-ajuda"
            />
          </AgnesTooltip>
        )}
      </div>
      <div className="flex items-center gap-6">
        <div className="relative h-40 w-40 shrink-0 rounded-full" style={{ background: conic }}>
          <div className="absolute inset-[22%] rounded-full bg-agnes-bg-default" />
        </div>
        <ul className="space-y-2 text-sm">
          {labels.map((label, i) => (
            <li key={label} className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: colors[i] }} />
              <span className="text-agnes-text">
                {label} (<b>{series[i] ?? 0}</b>/{total})
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// gráficos colapsável (agentes nocivos + alíquota de aposentadoria especial)
export function GraficosCard() {
  const [show, setShow] = React.useState(false);
  const { agentesDonut, aliquotDonut, loadingGraphs } = useAgentesNocivosStore();
  const semRegistros =
    !loadingGraphs &&
    agentesDonut.series.every((v) => v === 0) &&
    aliquotDonut.series.every((v) => v === 0);

  return (
    <div className="mt-4 rounded-xl border border-agnes-border bg-agnes-bg-muted">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-3 text-agnes-primary"
        onClick={() => setShow((s) => !s)}
      >
        <span className="font-medium">
          Gráficos de ensejador de aposentadoria especial e agentes nocivos{' '}
          {semRegistros && (
            <span className="font-normal text-agnes-text-muted">
              (Não há registros a serem exibidos)
            </span>
          )}
        </span>
        <span className="flex items-center gap-1 text-sm font-medium">
          {show ? 'Ocultar gráficos' : 'Exibir gráficos'}
          {show ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {show && (
        <div className="border-t border-agnes-border p-4">
          {loadingGraphs ? (
            <p className="py-8 text-center text-agnes-text-muted">Carregando gráficos…</p>
          ) : semRegistros ? (
            <div className="p-8 text-center text-agnes-text-muted">
              Não há registros a serem exibidos.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Donut
                title="Agentes nocivos"
                series={agentesDonut.series}
                labels={agentesDonut.labels}
                colors={agentesDonut.colors}
              />
              <Donut
                title="Ensejador Aposentadoria Especial"
                series={aliquotDonut.series}
                labels={aliquotDonut.labels}
                colors={aliquotDonut.colors}
                info={ENSEJADOR_APOSENTADORIA_INFO}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// popover Filtro da aba Trabalhadores (Agente nocivo / Aposentadoria especial / Contribuição-Alíquota / Risco)
function TrabalhadoresFilter() {
  const {
    agentOptions,
    nociveAgent,
    specialRetirement,
    retirementAliquot,
    setNociveAgent,
    setSpecialRetirement,
    setRetirementAliquot,
    setOnlyExposedRisks,
  } = useAgentesNocivosStore();
  const nFiltros = countFiltros({ nociveAgent, specialRetirement, retirementAliquot });
  const [open, setOpen] = React.useState(false);
  const [agentes, setAgentes] = React.useState<AgentOption[]>([]);
  const [especial, setEspecial] = React.useState('');
  const [tiers, setTiers] = React.useState<string[]>([]);

  // sempre que o popover abre, ressincroniza o estado local com o store —
  // sem isso "Aplicar" clobbera filtros já aplicados (ex.: via stat card)
  React.useEffect(() => {
    if (!open) return;
    setAgentes(agentOptions.filter((a) => nociveAgent.includes(a.value)));
    setEspecial(specialRetirement === null ? '' : specialRetirement ? 'sim' : 'nao');
    setTiers(retirementAliquot.map(aliquotKey));
  }, [open, agentOptions, nociveAgent, specialRetirement, retirementAliquot]);

  const toggleAgente = (a: AgentOption) => {
    setAgentes((prev) =>
      prev.some((p) => p.value === a.value)
        ? prev.filter((p) => p.value !== a.value)
        : [...prev, a],
    );
  };
  const toggleTier = (v: string) => {
    setTiers((prev) => (prev.includes(v) ? prev.filter((t) => t !== v) : [...prev, v]));
  };

  const aplicar = async () => {
    setOpen(false);
    await Promise.all([
      setNociveAgent(agentes.map((a) => a.value)),
      setSpecialRetirement(especial === '' ? null : especial === 'sim'),
      setRetirementAliquot(ALIQUOT_TIERS.filter((t) => tiers.includes(aliquotKey(t)))),
    ]);
  };
  const limpar = async () => {
    setAgentes([]);
    setEspecial('');
    setTiers([]);
    setOpen(false);
    await Promise.all([
      setNociveAgent([]),
      setSpecialRetirement(null),
      setRetirementAliquot([]),
      setOnlyExposedRisks(false),
    ]);
  };

  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          startIcon={<Filter className="h-4 w-4" />}
          aria-label={nFiltros > 0 ? `Filtro, ${nFiltros} ativos` : 'Filtro'}
        >
          {labelFiltro('Filtro', nFiltros)}
        </AgnesButton>
      </AgnesPopoverTrigger>
      <AgnesPopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-agnes-border px-4 py-3">
          <span className="text-base font-semibold text-agnes-text">Filtro</span>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-3 px-4 py-4">
          <div>
            <AgnesLabel>Agente nocivo</AgnesLabel>
            <AgnesAutocomplete
              options={agentOptions}
              value={agentes}
              onOptionToggle={toggleAgente}
              getOptionLabel={(a: AgentOption) => a.label}
              isOptionEqualToValue={(a: AgentOption, b: AgentOption) => a.value === b.value}
              placeholder="Selecione o agente nocivo"
              noOptionsText="Nenhum agente"
            />
          </div>
          <SelectField
            placeholder="Aposentadoria especial"
            value={especial}
            onValueChange={setEspecial}
            options={SIM_NAO_OPTIONS}
          />
          <div>
            <AgnesLabel>Contribuição / Alíquota</AgnesLabel>
            <div className="flex flex-col gap-1">
              {ALIQUOT_TIER_OPTIONS.map((o) => (
                <label key={o.value} className="flex items-center gap-2 text-sm text-agnes-text">
                  <input
                    type="checkbox"
                    checked={tiers.includes(o.value)}
                    onChange={() => toggleTier(o.value)}
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3">
          <AgnesButton variant="primary" onClick={aplicar} className="w-full">
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            variant="outlined"
            onClick={limpar}
            startIcon={<Trash2 className="h-4 w-4" />}
            className="w-full"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}

// indicador de tolerância (registeredLimit x consideredLimit). Largura uniforme + texto
// centralizado para "Dentro"/"Acima" ficarem alinhados entre si na coluna (CON-5243).
export function ToleranceIndicator({
  registeredLimit,
  consideredLimit,
}: {
  registeredLimit?: Any;
  consideredLimit?: Any;
}) {
  const registered = Number(registeredLimit);
  const considered = Number(consideredLimit);
  if (!Number.isFinite(registered) || !Number.isFinite(considered)) {
    return <span className="inline-block min-w-[8.5rem] text-center text-agnes-text-muted">-</span>;
  }
  const dentro = registered <= considered;
  return (
    <AgnesBadge
      variant={dentro ? 'success' : 'error'}
      className="min-w-[8.5rem] justify-center text-center"
      data-qa={`agentes-nocivos-tolerancia-${dentro ? 'dentro' : 'acima'}`}
    >
      {dentro ? 'Dentro da tolerância' : 'Acima da tolerância'}
    </AgnesBadge>
  );
}

// modal de detalhe dos agentes nocivos de um trabalhador
function DetalheAgentesModal({ row, onClose }: { row: Any | null; onClose: () => void }) {
  const agentes: Any[] = row?.nociveAgents ?? [];
  const columns: AgnesDataTableColumn<Any>[] = [
    { key: 'code', header: 'Código', width: 100 },
    { key: 'desc', header: 'Agente nocivo', width: 220 },
    { key: 'eventDesc', header: 'Evento', width: 140 },
    { key: 'initExposition', header: 'Início da exposição', width: 150 },
    {
      key: 'endExposition',
      header: 'Fim da exposição',
      width: 150,
      render: (r: Any) => r.endExposition || <span className="text-agnes-text-muted">-</span>,
    },
    { key: 'consideredLimit', header: 'Limite considerado', width: 150 },
    { key: 'registeredLimit', header: 'Limite registrado', width: 150 },
    { key: 'limitType', header: 'Tipo de limite', width: 140 },
    {
      key: 'tolerancia',
      header: 'Tolerância',
      width: 170,
      render: (r: Any) => (
        <ToleranceIndicator
          registeredLimit={r.registeredLimit}
          consideredLimit={r.consideredLimit}
        />
      ),
    },
  ];

  return (
    <AgnesModal
      open={row != null}
      title="Agentes nocivos do trabalhador"
      maxWidth="lg"
      onClose={onClose}
      footer={
        <div className="flex justify-end px-2">
          <AgnesButton variant="secondary" onClick={onClose}>
            Fechar
          </AgnesButton>
        </div>
      }
    >
      <div className="px-6 py-4">
        {row && (
          <p className="mb-3 text-sm text-agnes-text-muted">
            <span className="font-medium text-agnes-text">{row.name}</span> · {row.socialName}
          </p>
        )}
        {agentes.length === 0 ? (
          <EmptyState title="Nenhum agente nocivo para exibir" />
        ) : (
          <TableWrapper>
            <AgnesDataTable<Any>
              columns={columns}
              data={agentes}
              rowKey={(r) => `${r.code}-${r.eventDesc}-${r.initExposition}`}
              pagination={{
                page: 0,
                pageSize: agentes.length || 1,
                total: agentes.length,
                onPageChange: () => {},
                onPageSizeChange: () => {},
              }}
            />
          </TableWrapper>
        )}
      </div>
    </AgnesModal>
  );
}

// aba Trabalhadores — tabela server-side (retirement-risks)
function TrabalhadoresTab() {
  const {
    colabLista,
    colabTotal,
    colabPage,
    colabSize,
    colabSortBy,
    colabSortOrder,
    loadingColab,
    onlyExposedRisks,
    retirementRisksTotal,
    searchColaborador,
    setOnlyExposedRisks,
    setColabPage,
    setColabSize,
    setColabSort,
    filterFromSpecial,
    exportandoColab,
    exportarColaboradores,
  } = useAgentesNocivosStore();
  const [busca, setBusca] = React.useState('');
  const [detalhe, setDetalhe] = React.useState<Any | null>(null);
  const { hiddenColumnKeys, toggleColumn } = useColumnPreferences(
    'saude-seguranca-agentes-trabalhadores',
  );

  const columns: AgnesDataTableColumn<Any>[] = [
    { key: 'socialName', header: 'Empresa', width: 220, sortable: true },
    { key: 'cnpj', header: 'CNPJ', width: 150, sortable: true },
    { key: 'matriculation', header: 'Matrícula', width: 120, sortable: true },
    { key: 'name', header: 'Nome', width: 200, sortable: true },
    { key: 'cpf', header: 'CPF', width: 140, sortable: true },
    { key: 'competency', header: 'Competência', width: 120, sortable: true },
    { key: 'categoryType', header: 'Tipo da categoria', width: 150, sortable: true },
    { key: 'categoryCode', header: 'Cód. Categoria', width: 130, sortable: true },
    { key: 'categoryInfo', header: 'Categoria', width: 200, sortable: true },
    {
      key: 'nociveAgent',
      header: 'Agente nocivo',
      width: 140,
      render: (r: Any) =>
        r.nociveAgents?.length ? (
          <AgnesBadge variant="warning">Sim</AgnesBadge>
        ) : (
          <AgnesBadge variant="secondary">Não</AgnesBadge>
        ),
    },
    {
      key: 'specialRetirement',
      header: 'Aposentadoria especial',
      width: 170,
      render: (r: Any) => (r.specialRetirement ? 'Sim' : 'Não'),
    },
    { key: 'retirementAliquot', header: 'Contribuição / Alíquota', width: 200, sortable: true },
    { key: 'lastUpdate', header: 'Atualização', width: 150, sortable: true },
    {
      key: 'situation',
      header: 'Situação',
      width: 130,
      render: (r: Any) => <SituacaoBadge situation={r.situation} />,
    },
    { key: 'admissionDate', header: 'Admissão', width: 130, sortable: true },
    {
      key: 'detachDate',
      header: 'Desligamento',
      width: 130,
      render: (r: Any) => r.detachDate || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'acoes',
      header: 'Ações',
      width: 90,
      align: 'center',
      toggleable: false,
      render: (r: Any) => (
        <AgnesIconButton
          aria-label="Ver agentes nocivos"
          size="sm"
          disabled={!r.nociveAgents?.length}
          onClick={() => setDetalhe(r)}
        >
          <Eye className="h-4 w-4" />
        </AgnesIconButton>
      ),
    },
  ];

  return (
    <>
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <StatDonut
          title="Trabalhadores com aposentadoria especial"
          value={retirementRisksTotal}
          pctLabel="Trabalhadores com risco identificado"
          onClick={() => filterFromSpecial()}
        />
        <ExposicaoCard />
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-[320px] flex-1">
          <SearchInput
            value={busca}
            onValueChange={setBusca}
            onSearch={() => searchColaborador(busca)}
            placeholder="Pesquisar por nome, matrícula ou CPF"
          />
        </div>
        <TrabalhadoresFilter />
        <label className="flex items-center gap-2 text-sm text-agnes-text">
          <input
            type="checkbox"
            checked={onlyExposedRisks}
            onChange={(e) => setOnlyExposedRisks(e.target.checked)}
          />
          Risco apresentado
        </label>
        <ColumnsMenu
          columns={columns}
          hiddenColumnKeys={hiddenColumnKeys}
          onToggleColumn={toggleColumn}
          data-qa="agentes-nocivos-colunas"
        />
        <ExportButton
          variant="outlined"
          startIcon={<Download className="h-4 w-4" />}
          disabled={exportandoColab}
          onClick={exportarColaboradores}
        >
          Exportar
        </ExportButton>
      </div>

      {!loadingColab && colabLista.length === 0 ? (
        <EmptyState className="mt-4" title="Nenhum trabalhador para exibir" />
      ) : (
        <TableWrapper className="mt-4">
          <AgnesDataTable<Any>
            columns={columns}
            hiddenColumnKeys={hiddenColumnKeys}
            data={colabLista}
            rowKey={(r) => String(r.id ?? `${r.cpf}-${r.competency}`)}
            loading={loadingColab}
            sort={{
              sortBy: colabSortBy ?? '',
              sortOrder: colabSortOrder,
              onSortChange: setColabSort,
            }}
            pagination={{
              page: colabPage,
              pageSize: colabSize,
              total: colabTotal,
              serverSide: true,
              onPageChange: setColabPage,
              onPageSizeChange: setColabSize,
            }}
          />
        </TableWrapper>
      )}

      <DetalheAgentesModal row={detalhe} onClose={() => setDetalhe(null)} />
    </>
  );
}

// card "Trabalhadores com risco de exposição" (separado p/ reatividade granular do %)
function ExposicaoCard() {
  const { exposedCollaborators, filterFromExposed } = useAgentesNocivosStore();
  return (
    <StatDonut
      title="Trabalhadores com risco de exposição"
      value={exposedCollaborators.exposed}
      pctLabel={`${exposedCollaborators.percentage ?? 0}% do total (${exposedCollaborators.exposed}/${exposedCollaborators.total})`}
      onClick={() => filterFromExposed()}
    />
  );
}

// aba Agentes Nocivos — tabela server-side (nocive-agents)
function AgentesNocivosTab() {
  const {
    agLista,
    agTotal,
    agPage,
    agSize,
    agSortBy,
    agSortOrder,
    loadingAg,
    searchAgente,
    setAgPage,
    setAgSize,
    setAgSort,
    exportandoAg,
    exportarAgentes,
  } = useAgentesNocivosStore();
  const [busca, setBusca] = React.useState('');
  const { hiddenColumnKeys, toggleColumn } = useColumnPreferences(
    'saude-seguranca-agentes-nocivos',
  );

  const columns: AgnesDataTableColumn<Any>[] = [
    { key: 'socialName', header: 'Empresa', width: 220, sortable: true },
    { key: 'cnpj', header: 'CNPJ', width: 150, sortable: true },
    { key: 'code', header: 'Código', width: 100, sortable: true },
    { key: 'desc', header: 'Agente Nocivo', width: 220, sortable: true },
    { key: 'collaborators', header: 'Trabalhadores', width: 130, sortable: true },
    { key: 'consideredLimit', header: 'Limite de tolerância', width: 160, sortable: true },
    { key: 'evaluationCriteria', header: 'Critério de avaliação', width: 180 },
    { key: 'minorRegisteredLimit', header: 'Menor limite', width: 130, sortable: true },
    { key: 'majorRegisteredLimit', header: 'Maior limite', width: 130, sortable: true },
    { key: 'lastUpdate', header: 'Atualização', width: 150 },
  ];

  return (
    <>
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-[320px] flex-1">
          <SearchInput
            value={busca}
            onValueChange={setBusca}
            onSearch={() => searchAgente(busca)}
            placeholder="Pesquisar por código ou agente nocivo"
          />
        </div>
        <ColumnsMenu
          columns={columns}
          hiddenColumnKeys={hiddenColumnKeys}
          onToggleColumn={toggleColumn}
          data-qa="agentes-nocivos-agente-colunas"
        />
        <ExportButton
          variant="outlined"
          startIcon={<Download className="h-4 w-4" />}
          disabled={exportandoAg}
          onClick={exportarAgentes}
        >
          Exportar
        </ExportButton>
      </div>
      {!loadingAg && agLista.length === 0 ? (
        <EmptyState className="mt-4" title="Nenhum agente nocivo para exibir" />
      ) : (
        <TableWrapper className="mt-4">
          <AgnesDataTable<Any>
            columns={columns}
            hiddenColumnKeys={hiddenColumnKeys}
            data={agLista}
            rowKey={(r) => String(r.id ?? `${r.cnpj}-${r.code}`)}
            loading={loadingAg}
            sort={{
              sortBy: agSortBy ?? '',
              sortOrder: agSortOrder,
              onSortChange: setAgSort,
            }}
            pagination={{
              page: agPage,
              pageSize: agSize,
              total: agTotal,
              serverSide: true,
              onPageChange: setAgPage,
              onPageSizeChange: setAgSize,
            }}
          />
        </TableWrapper>
      )}
    </>
  );
}

export default function PageAgentesNocivos() {
  const [tab, setTab] = React.useState('trabalhadores');
  const init = useAgentesNocivosStore((s) => s.init);
  const colabTotal = useAgentesNocivosStore((s) => s.colabTotal);
  const agTotal = useAgentesNocivosStore((s) => s.agTotal);

  React.useEffect(() => {
    init();
    useEmpregadoresStore.getState().buscar();
  }, [init]);

  return (
    <>
      <AgnesPageHeader
        icon={<ShieldAlert />}
        title="Agentes nocivos e aposentadoria especial"
        subtitle="Monitoramento de risco de exposição profissional e aposentadoria especial"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Saúde e segurança' },
          { label: 'Agentes nocivos' },
        ]}
      />

      <div className="mt-2 rounded-2xl border border-agnes-border bg-agnes-bg-surface p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-agnes-text">
            <b>Para visualizar gráficos e registros em tabela</b>, selecione uma competência no
            filtro:
          </p>
          <FiltroGeral />
        </div>

        <GraficosCard />

        {/* tabs Trabalhadores / Agentes Nocivos */}
        <div className="mt-4">
          <AgnesTabs value={tab} onValueChange={setTab}>
            <AgnesTabsList>
              <AgnesTabsTrigger value="trabalhadores">
                Trabalhadores ({colabTotal})
              </AgnesTabsTrigger>
              <AgnesTabsTrigger value="agentes">Agentes Nocivos ({agTotal})</AgnesTabsTrigger>
            </AgnesTabsList>

            <AgnesTabsContent value="trabalhadores" className="pt-4">
              <TrabalhadoresTab />
            </AgnesTabsContent>

            <AgnesTabsContent value="agentes" className="pt-4">
              <AgentesNocivosTab />
            </AgnesTabsContent>
          </AgnesTabs>
        </div>
      </div>
    </>
  );
}
